import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCContext } from '../CC/CCContext';
import styles from './User.module.css';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import AlbumEditor from '../AlbumEditor/AlbumEditor';
import UserManagement from './UserManagement';
import EditAccount from './ChangePassword';
import CreateAccount from './CreateAccount';
import ForgotPassword from './ForgotPassword';

function User() {
    const { t } = useTranslation();
    const { user, setUser } = useCCContext();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
    const [showAlbumEditorModal, setShowAlbumEditorModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [showUserManagementModal, setShowUserManagementModal] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const menuButtonRef = useRef<HTMLButtonElement | null>(null);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [messageVisible, setMessageVisible] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const warningTimerRef = useRef<number | null>(null);
    const logoutTimerRef = useRef<number | null>(null);
    //const [messageToShow,setMessageToShow] = useState(false);
    const [showPassword, setShowPassword] = useState(false)
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    useEffect(() => {
        return () => {
            if (warningTimerRef.current) {
                clearTimeout(warningTimerRef.current);
            }
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }
        };
    }, []);

    const handleMessage = (message: string)=>{
        setMessage(message);
        setMessageVisible(true);

        setTimeout(() => {
            setMessageVisible(false);
        }, 3000);
    }

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoginError(null);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=login&email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
            );
            const respText = await response.text();
            if (respText === "Invalid Credentials") {
                setLoginError(t("error_login_invalidcredentials"));
            } else {
                const data = JSON.parse(respText);
                //console.log('Login response:', data);
                if (response.ok && data) {
                    setUser(data);
                    setShowLoginModal(false);
                } else {
                    if (data.message === "Invalid Credentials") {
                        setLoginError(t("error_login_invalidcredentials"));
                    } else {
                        setLoginError(t("error_login_unexpected"));
                    }
                }
            }
        } catch (error) {
            console.error(t("error_login"), error);
            setLoginError(t("error_login_connection_failed"));
        }
    };

    useEffect(() => {
        if (user?.tokenExpires) {
            const expirationTime = new Date(user.tokenExpires).getTime();
            const currentTime = new Date().getTime();
            const timeRemaining = expirationTime - currentTime;

            if (warningTimerRef.current) {
                clearTimeout(warningTimerRef.current);
            }
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }

            warningTimerRef.current = setTimeout(() => {
                setMessage(t("message_loggedout_soon"));
                setMessageVisible(true);

                setTimeout(() => {
                    setMessageVisible(false);
                }, 3000);

            }, timeRemaining - 60000) as unknown as number;

            logoutTimerRef.current = setTimeout(() => {
                setUser(null);
                setShowMenuDropdown(false);
                setMessage(t("message_loggedout"));
                setMessageVisible(true);

                setTimeout(() => {
                    setMessageVisible(false);
                }, 3000);
            }, timeRemaining) as unknown as number;
        }
    }, [user?.tokenExpires, t]);

    const renderUserMenu = () => {
        let x = 0;
        let y = 0;

        if (menuButtonRef.current) {
            const rect = menuButtonRef.current.getBoundingClientRect();

            const parentModal = menuButtonRef.current.parentElement;
            if (parentModal) {
                const modalRect = parentModal.getBoundingClientRect();
                x = rect.left - modalRect.left - 108;
                y = rect.bottom + 2 - modalRect.top + 12;
            }
        }

        const menuItems = [
            {
                label: t("user_edit"), onClick: () => {
                    //console.log('Edit clicked');
                    setShowEditUserModal(true);
                    setShowMenuDropdown(false);
                }
            },

            {
                label: t("user_album_editor"), onClick: () => {
                    setShowAlbumEditorModal(true);
                    setShowMenuDropdown(false);
                }
            },

            {
                label: t("user_logout"), onClick: () => {
                    setUser(null);
                    setShowMenuDropdown(false);
                }
            }
        ];

        if (user?.userDetails?.roles?.includes('admin')) {
            menuItems.splice(1, 0, {
                label: t("user_manage"), onClick: () => {
                    //console.log('Manage Users clicked');
                    setShowUserManagementModal(true);
                    setShowMenuDropdown(false);
                }
            });
        }

        return (
            <div
                className={styles.userMenuResults}
                style={{ position: 'absolute', top: y + 'px', left: x + 'px' }}
            >
                {menuItems.map((item, index) => (
                    <div
                        key={index}
                        className={styles.userMenuItem}
                        onClick={item.onClick}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuButtonRef.current &&
                !menuButtonRef.current.contains(event.target as Node) &&
                !document.querySelector(`.${styles.userMenuResults}`)?.contains(event.target as Node)
            ) {
                setShowMenuDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.User}>
            {user?.userDetails?.realname ? (
                <div className={styles.userdata}>
                    <div className={styles.realname}>{user.userDetails?.realname}</div>
                    <button
                        className={styles.menubutton}
                        ref={menuButtonRef}
                        onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                        aria-haspopup="true"
                        aria-expanded={showMenuDropdown}
                    >
                        <GenericIconButton
                            icon='regular/chevron-double-down.svg'
                            hoverEnabled={false}
                            width='8px'
                            height='8px'
                            fill='#fff'
                        />
                    </button>
                    {showMenuDropdown && renderUserMenu()}
                </div>
            ) : (
                <>
                    <button className={styles.loginButton} onClick={() => setShowLoginModal(true)}>
                    {t("login_popup_button")}
                    </button>
                    {showLoginModal && (
                        <div className={styles.popupoverlay}>
                            <div className={styles.popup} role="dialog" aria-modal="true" aria-labelledby="login-title">
                                <div className={styles.header}>
                                    <h2 id="login-title">{t("login_popup_title")}</h2>
                                    <button
                                        className={styles.closeButton}
                                        onClick={() => { setShowLoginModal(false); setLoginError(null); }}
                                        aria-label="Close login modal"
                                    >
                                        &times;
                                    </button>
                                </div>
                                <div className={styles.main}>
                                    {loginError && (
                                        <div className={styles.errorMessage}>
                                            {loginError}
                                        </div>
                                    )}
                                    <form onSubmit={handleLogin} className={styles.loginForm}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="email">{t("login_email")}</label>
                                            <input
                                                type="text"
                                                id="email"
                                                placeholder={t("login_email")}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="password">{t("login_password")}</label>
                                            <div>
                                                <input
                                                    type={showPassword ? 'text':'password'}
                                                    id="password"
                                                    placeholder={t("login_password")}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                    style={{marginRight: '10px'}}
                                                />
<GenericIconButton 
                    icon={showPassword ? 'light/eye.svg':'light/eye-slash.svg'}
                    hoverEnabled={false}
                    width='16px' 
                    height='16px'
                    onClick={() => {handleClickShowPassword()}}
                />                                                
                                            </div>
                                        </div>
                                        <div className={styles.footer}>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.loginSubmitButton}`}
                                                type="button"
                                                onClick={() => {
                                                    setShowLoginModal(false);
                                                    setShowForgotPasswordModal(true);
                                                }}
                                            >
                                                Mot de passe oublié
                                            </button>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.loginSubmitButton}`}
                                                type="submit"
                                            >
                                                {t("login_submit")}
                                            </button>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.createAccountButton}`}
                                                type="button"
                                                onClick={() => {
                                                    setShowLoginModal(false);
                                                    setShowCreateAccountModal(true);
                                                }}
                                            >
                                                {t("login_create")}
                                            </button>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.cancelButton}`}
                                                type="button"
                                                onClick={() => { setShowLoginModal(false); setLoginError(null); }}
                                            >
                                                {t("login_cancel")}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className={`${styles.message} ${messageVisible ? styles.fadeIn : styles.fadeOut}`}>
                {message}
            </div>

            {showAlbumEditorModal && (
                <div className={styles.albumEditor}>
                    <AlbumEditor onClose={() => { setShowAlbumEditorModal(false) }} onMessage={handleMessage}/>
                </div>
            )}

            {showUserManagementModal && (
                <div className={styles.userManagement}>
                    <UserManagement onClose={() => { setShowUserManagementModal(false) }} />
                </div>
            )}

            {showCreateAccountModal && (
                <CreateAccount onClose={() => { setShowCreateAccountModal(false) }} />
            )}

            {showEditUserModal && (
                <EditAccount onClose={() => { setShowEditUserModal(false) }} user={user} />
            )}

            {showForgotPasswordModal && (
                <ForgotPassword onClose={() => { setShowForgotPasswordModal(false) }}  onMessage={handleMessage}/>
            )}
        </div>
    );
}

export default User;
